import React, { useState, useEffect, useMemo } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import CartButton from './CartButton';
import SearchInput from './SearchInput';
import useCartStore from '../store/cartStore';
import useUserStore from '../store/userStore';
import CartModal from './CartModal';
import UserMenu from './UserMenu';
import authService from '../services/authService';

const SCROLL_START = 100;
const SCROLL_END = 800;

function Header({ onMenuToggle, serverAuthState = null }) {
  const router = useRouter();
  const isIndexPage = router.pathname === '/';
  const cartItems = useCartStore((state) => state.cartItems);
  const isCartOpen = useCartStore((state) => state.isOpen);
  const { user, isLoggedIn, loading } = useUserStore();
  
  const itemCount = cartItems.length;
  const [animate, setAnimate] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [opacity, setOpacity] = useState(isIndexPage ? 0 : 0.8);
  const [isAuthChecked, setIsAuthChecked] = useState(false);

  // Проверяем авторизацию при загрузке
  useEffect(() => {
    const checkAuth = async () => {
      if (!isAuthChecked) {
        await authService.getValidToken();
        setIsAuthChecked(true);
      }
    };
    
    checkAuth();
  }, [isAuthChecked]);

  // Определяем пользователя из серверного состояния или из хранилища
  const displayUser = useMemo(() => {
    if (user) return user;
    if (serverAuthState?.user) {
      return {
        ...serverAuthState.user,
        photoURL: serverAuthState.user.photo_url,
        displayName: serverAuthState.user.displayName || serverAuthState.user.email
      };
    }
    return null;
  }, [user, serverAuthState]);

  // Определяем авторизован ли пользователь - из серверного состояния или из хранилища
  const isAuthenticated = useMemo(() => {
    // Если загрузка данных еще идет и у нас есть серверные данные, используем их
    if (loading && serverAuthState) {
      return serverAuthState?.isAuthenticated === true;
    }
    
    // После завершения загрузки приоритет у клиентского состояния
    if (!loading) {
      return isLoggedIn;
    }
    
    // Если нет клиентских данных, используем серверные
    return serverAuthState?.isAuthenticated === true;
  }, [isLoggedIn, serverAuthState, loading]);

  const calculateOpacity = (scrollTop) => {
    if (scrollTop >= SCROLL_START && scrollTop <= SCROLL_END) {
      const progress = (scrollTop - SCROLL_START) / (SCROLL_END - SCROLL_START);
      return progress;
    }
    return scrollTop < SCROLL_START ? 0 : 1;
  };

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      if (isIndexPage) {
        setOpacity(calculateOpacity(scrollTop));
        setIsScrolled(scrollTop > SCROLL_START);
      } else {
        setIsScrolled(scrollTop > 0);
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isIndexPage]);

  useEffect(() => {
    if (itemCount > 0) {
      setAnimate(true);
      const timer = setTimeout(() => setAnimate(false), 500);
      return () => clearTimeout(timer);
    }
  }, [itemCount]);

  // Обработчик для навигации на страницу login
  const handleSignInClick = () => {
    router.push('/login');
  };

  const bgOpacityClass = useMemo(() => {
    if (isCartOpen) return 'bg-white';
    if (!isIndexPage) return 'bg-white bg-opacity-80';
    if (opacity <= 0) return 'bg-transparent';
    if (opacity >= 1) return 'bg-white';
    if (opacity < 0.25) return 'bg-white bg-opacity-10';
    if (opacity < 0.5) return 'bg-white bg-opacity-30';
    if (opacity < 0.75) return 'bg-white bg-opacity-50';
    return 'bg-white bg-opacity-70';
  }, [isCartOpen, isIndexPage, opacity]);

  const blurClass = useMemo(() => {
    if (isCartOpen) return '';
    return isScrolled ? 'backdrop-blur-md' : '';
  }, [isScrolled, isCartOpen]);

  const logoContainerClasses = "h-14 flex items-center";
  const buttonContainerClasses = "h-14 flex justify-end items-center";

  return (
    <>
      <div className={`fixed top-0 left-0 w-full z-50 transition-colors duration-300 ${bgOpacityClass} ${blurClass}`}>
        <div className="w-full">
          <div className="max-w-7xl w-[90%] mx-auto py-4">
            {/* Mobile Layout */}
            <div className="grid grid-cols-2 md:hidden gap-y-2">
              <div className={logoContainerClasses}>
                <Link href="/" className="text-base font-bold text-gray-800 no-underline">
                  davooda
                </Link>
              </div>
              
              <div className={buttonContainerClasses}>
                <Link 
                  href="/pricing" 
                  className="text-gray-800 hover:text-black font-medium no-underline mr-6"
                >
                  Pricing
                </Link>
                
                <div className={`flex items-center justify-center rounded-full bg-black text-white w-5 h-5 text-xs font-bold mt-1 ${animate ? 'animate-pop' : ''}`}>
                  {itemCount}
                </div>
                
                <div className="mr-4" id="cartIcon">
                  <CartButton />
                </div>
                
                <div className="transition-opacity duration-300 ease-in-out min-w-[40px] flex justify-end">
                  {isAuthenticated && displayUser ? (
                    <UserMenu user={displayUser} />
                  ) : (
                    <button 
                      onClick={handleSignInClick}
                      className="text-black border border-white py-1 px-3 rounded-md text-sm font-medium hover:bg-gray-100 transition duration-300"
                    >
                      Sign In
                    </button>
                  )}
                </div>
              </div>
              
              <div className="col-span-2 mt-2">
                <SearchInput />
              </div>
            </div>
            
            {/* Desktop Layout */}
            <div className="hidden md:grid md:grid-cols-8 items-center">
              <div className={`col-span-2 ${logoContainerClasses}`}>
                <Link href="/" className="text-base font-bold text-gray-800 no-underline">
                  davooda
                </Link>
              </div>
              
              <div className="col-span-4 h-14 flex items-center">
                <SearchInput />
              </div>
              
              <div className={`col-span-2 ${buttonContainerClasses}`}>
                {/* never delete it. I need it in future */}
                {/* <Link 
                  href="/pricing" 
                  className="text-gray-800 hover:text-black font-medium no-underline mr-6"
                >
                  Pricing
                </Link> */}
                
                <div className={`flex items-center justify-center rounded-full bg-black text-white w-5 h-5 text-xs font-bold mt-1 ${animate ? 'animate-pop' : ''}`}>
                  {itemCount}
                </div>
                
                <div className="mr-4" id="cartIcon">
                  <CartButton />
                </div>
                
                <div className="transition-opacity duration-300 ease-in-out min-w-[40px] flex justify-end">
                  {isAuthenticated && displayUser ? (
                    <UserMenu user={displayUser} />
                  ) : (
                    <button 
                      onClick={handleSignInClick}
                      className="text-black border border-white py-1 px-3 rounded-md text-sm font-medium hover:bg-gray-100 transition duration-300"
                    >
                      Sign In
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <CartModal />
    </>
  );
}

export default Header;